// AskBigAgri — the ⌘K palette's Ask view (spec P0.3, voice-enabled in P1).
//
// Renders a question's streamed answer from the ask-bigagri gateway. The SSE
// client lives in src/lib/useAskStream (shared with the /ask floor mode);
// the voice-out layer (sentence-chunked speechSynthesis) is src/lib/speech.
// Deep links come from the TOOLS' own {link} fields — never model text —
// validated against LINK_ALLOWLIST before rendering.
//
// Failure states are honest and specific: not subscribed (403), monthly cap
// (429), not configured (501), offline, network drop mid-stream. The answer
// can be wrong; the link to the real page is always right — that is the
// advisor contract.

import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Sparkles, Volume2, VolumeX, Wrench, X } from 'lucide-react'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { logHelpEvent } from '@/lib/helpEvents'
import { createSentenceSpeaker, speechOutputSupported, type SentenceSpeaker } from '@/lib/speech'
import { createCloudSpeaker } from '@/lib/cloudTts'
import { askHistory, pushAskTurn } from '@/lib/askConversation'
import { useAskStream, TOOL_LABEL } from '@/lib/useAskStream'
import { askLinkLabel } from '@/lib/askLinkLabel'

// The Ask view — swapped in for the palette list while a question is live.
// fallow-ignore-next-line complexity
export function AskBigAgriView({ question, route, autoSpeak, onBack, onClose, onNavigate }: {
  question:   string
  route:      string
  autoSpeak?: boolean
  onBack:     () => void
  onClose:    () => void
  onNavigate: (path: string) => void
}) {
  const [attempt, setAttempt] = useState(0)
  // Voice-out: sticky preference; a spoken question forces it on this once.
  // `muted` is the per-view override on top — without it, muting a
  // voice-initiated ask (voicePref already false, autoSpeak true) was a no-op.
  const [voicePref, setVoicePref] = usePersistedState<boolean>('kf49.askSpeak', false)
  const [muted, setMuted] = useState(false)
  const [voiceRate] = usePersistedState<number>('kf49.askVoiceRate', 1.05)
  const [voiceName] = usePersistedState<string>('kf49.askVoiceName', '')
  const [voiceEngine] = usePersistedState<'cloud' | 'device'>('kf49.askVoiceEngine', 'cloud')
  const voiceOn = !muted && (voicePref || !!autoSpeak) && speechOutputSupported()
  const speakerRef = useRef<SentenceSpeaker | null>(null)
  const voiceOnRef = useRef(voiceOn); voiceOnRef.current = voiceOn
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null

  // One speaker per question/attempt; silenced on unmount, back, close, mute.
  // Device rate/voice keep applying to device mode AND the cloud fallback;
  // cloud applies rate via playbackRate and has its own voice.
  const makeDeviceSpeaker = () => createSentenceSpeaker({ rate: voiceRate, voiceName: voiceName || undefined })
  const makeSpeaker = () => voiceEngine === 'cloud'
    ? createCloudSpeaker({ rate: voiceRate }, makeDeviceSpeaker)
    : makeDeviceSpeaker()
  useEffect(() => {
    speakerRef.current = makeSpeaker()
    return () => speakerRef.current?.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, attempt])

  // Follow-up context: snapshot the conversation BEFORE this question; the
  // exchange is pushed once the answer completes.
  const historyRef = useRef(askHistory(tenantId))
  const state = useAskStream(
    question, route, attempt,
    (delta) => { if (voiceOnRef.current) speakerRef.current?.feed(delta) },
    (answer) => {
      if (voiceOnRef.current) speakerRef.current?.flush()
      pushAskTurn(tenantId, 'user', question)
      pushAskTurn(tenantId, 'assistant', answer)
    },
    historyRef.current,
  )
  // Auto-scroll only when already pinned near the bottom — never yank the
  // view back down while the user is reading something they scrolled up to.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTo({ top: el.scrollHeight })
  }, [state.text])

  // Spoken failure cue: if this ask was being spoken, say the failure too —
  // one short sentence before the visual error does the rest.
  useEffect(() => {
    if (state.phase === 'error' && voiceOnRef.current) {
      speakerRef.current?.feed('Sorry — that failed. Tap retry. ')
      speakerRef.current?.flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase])

  const tapLink = (path: string) => {
    logHelpEvent({ eventType: 'ask_link_tap', route, queryText: path })
    speakerRef.current?.cancel()
    onNavigate(path)
  }
  const leave = (fn: () => void) => () => { speakerRef.current?.cancel(); fn() }
  // Mute stops the audio directly rather than cancel()ing the speaker — the
  // speaker's cancelled latch is permanent, and unmuting must resume speaking
  // from the NEXT chunk (muted chunks are simply never fed; no replay).
  const toggleVoice = () => {
    if (voiceOn) {
      if (speechOutputSupported()) window.speechSynthesis.cancel()
      // Cloud engine: speechSynthesis.cancel() can't stop the cloud speaker's
      // audio or drop its fed-but-unspoken buffer — swap in a fresh speaker so
      // the sound stops NOW and unmuting speaks from the next chunk.
      if (voiceEngine === 'cloud') { speakerRef.current?.cancel(); speakerRef.current = makeSpeaker() }
      setMuted(true)
      setVoicePref(false)
    } else {
      setMuted(false)
      setVoicePref(true)
    }
  }
  const retry = () => {
    logHelpEvent({ eventType: 'ask_retry', route: route + (autoSpeak ? '#voice' : ''), queryText: question })
    setAttempt(a => a + 1)
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Ask BigAgri"
      onClick={leave(onClose)}
      data-theme="dark"
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 pt-[12vh]">
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-xl surface-sunk border hairline-strong rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 border-b hairline px-3 py-2.5">
          <button onClick={leave(onBack)} className="btn-ghost btn-sm shrink-0" aria-label="Back to search">
            <ArrowLeft size={15} />
          </button>
          <Sparkles size={14} className="text-violet-400 shrink-0" />
          <button onClick={leave(onBack)} title="Edit this question"
            className="flex-1 text-left text-sm text-white truncate hover:text-violet-200">{question}</button>
          {speechOutputSupported() && (
            <button onClick={toggleVoice} className="btn-ghost btn-sm shrink-0"
              aria-label={voiceOn ? 'Stop speaking answers' : 'Speak answers'}
              title={voiceOn ? 'Speaking answers — tap to mute' : 'Speak answers aloud'}>
              {voiceOn ? <Volume2 size={15} className="text-violet-300" /> : <VolumeX size={15} />}
            </button>
          )}
          <button onClick={leave(onClose)} className="btn-ghost btn-sm shrink-0" aria-label="Close"><X size={15} /></button>
        </div>

        <div ref={scrollRef} className="max-h-[50dvh] overflow-y-auto px-4 py-3 space-y-3">
          {state.tools.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {state.tools.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] text-fg-muted surface border hairline-strong rounded-full px-2 py-0.5">
                  <Wrench size={10} /> {TOOL_LABEL[t] ?? t}
                </span>
              ))}
            </div>
          )}

          {state.text
            ? <p className="text-sm text-fg-secondary whitespace-pre-wrap">{state.text}{state.phase === 'streaming' && <span className="animate-pulse">▍</span>}</p>
            : state.phase === 'streaming'
              ? <p className="text-sm text-fg-muted animate-pulse">Looking that up…</p>
              : null}

          {state.phase === 'error' && (
            <div className="space-y-2">
              <p className="text-sm text-amber-300">{state.error}</p>
              <button onClick={retry} className="btn-secondary btn-sm">Retry</button>
            </div>
          )}

          {state.phase === 'done' && state.links.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {state.links.map(l => (
                <button key={l} onClick={() => tapLink(l)}
                  className="tap-target-y inline-flex items-center gap-1.5 text-xs text-blue-300 bg-blue-950/40 border border-blue-800 rounded-lg px-2.5 py-1.5 hover:bg-blue-900/40">
                  {askLinkLabel(l)} <ArrowRight size={12} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t hairline px-4 py-2 text-[11px] text-fg-muted">
          Answers come from your live data — tap through to verify. The assistant can only read what you can.
        </div>
      </div>
    </div>
  )
}
